// Svelte-free by design, so a `node:test` file can exercise it.

/**
 * Lo stato di pagamento di una fattura, lato interfaccia.
 *
 * Lo stato lo calcola il server (src/domain/payment-status.js) e arriva in
 * `pagamento` su ogni riga: qui si decide soltanto come mostrarlo e quando
 * offrire «Registra pagamento».
 */

/** Valori del filtro «Pagamento» della pagina Fatture; '' = nessun filtro. */
export const PAGAMENTO_FILTER_VALUES = Object.freeze(['', 'da_saldare', 'scadute', 'saldate']);

/** L'etichetta di ogni valore del filtro. */
export const PAGAMENTO_FILTER_LABELS = Object.freeze({
  '': 'invoices.pagamento.filter.all',
  da_saldare: 'invoices.pagamento.filter.daSaldare',
  scadute: 'invoices.pagamento.filter.scadute',
  saldate: 'invoices.pagamento.filter.saldate',
});

/**
 * Gli stati «da saldare». Copia speculare di PAGAMENTO_FILTERS.da_saldare in
 * src/domain/payment-status.js: test/frontend-payment-status.test.js le tiene allineate.
 */
export const OPEN_STATES = Object.freeze(['aperta', 'parziale', 'scaduta', 'senzaScadenze']);

/** `?pagamento=` → un valore del filtro; quello che non conosce diventa «Tutte». */
export function seedPagamentoFilter(raw) {
  return PAGAMENTO_FILTER_VALUES.includes(raw) ? raw : '';
}

/** Si può registrare un pagamento: la fattura è aperta e resta qualcosa da saldare. */
export function isPaymentOpen(pagamento) {
  return Boolean(pagamento) && OPEN_STATES.includes(pagamento.stato) && Number(pagamento.residuo) > 0;
}

/**
 * Come si è mosso davvero il denaro. Copia speculare di METODI_EFFETTIVI in
 * src/domain/metodo-effettivo.js: test/frontend-payment-status.test.js le tiene
 * allineate. Non è la modalità di pagamento scritta in fattura (MP01…MP23), che
 * dice come le parti avevano concordato di pagare e non cambia.
 */
export const METODI_EFFETTIVI = Object.freeze([
  'contanti', 'bonifico', 'carta', 'addebito_diretto', 'assegno', 'compensazione', 'anticipo_fatture', 'altro',
]);

/** L'etichetta di ogni metodo. */
export const METODO_EFFETTIVO_LABELS = Object.freeze(Object.fromEntries(
  METODI_EFFETTIVI.map((metodo) => [metodo, `invoices.pagamento.metodo.${metodo}`]),
));

/** La chiave dell'etichetta di un metodo; null se non indicato o sconosciuto. */
export function metodoEffettivoLabel(metodo) {
  return METODO_EFFETTIVO_LABELS[metodo] ?? null;
}

/**
 * Una rata da mostrare come pagata: ha una data di pagamento, oppure è di 0,00
 * e non chiede niente — come la salda il server (instalmentSettlement in
 * src/domain/payment-status.js).
 */
export function instalmentShownPaid(tranche) {
  if (tranche?.dataPagamento) return true;
  return tranche?.importo != null && tranche.importo !== '' && Number(tranche.importo) <= 0;
}

/**
 * Quali azioni offrire su una rata nella tabella del dettaglio.
 *
 * «Segna come pagata» solo se la rata chiede ancora denaro (non è già mostrata
 * come pagata); «Annulla pagamento» solo se c'è davvero qualcosa di registrato
 * — una data, un importo pagato o un metodo — come per `cancel_payment` sul
 * server. Una rata di 0,00 mai toccata non ha né l'una né l'altra.
 *
 * @returns {{markPaid: boolean, markUnpaid: boolean}}
 */
export function instalmentActions(tranche) {
  const recorded = Boolean(tranche?.dataPagamento) || tranche?.importoPagato != null || tranche?.metodoEffettivo != null;
  return { markPaid: !instalmentShownPaid(tranche), markUnpaid: recorded };
}

/**
 * Se la pagina della fattura mostra la scheda delle rate. Un'autofattura
 * (TD16–TD23, TD28), emessa o ricevuta in copia, non è mai un debito né un
 * credito: le rate che il suo XML eventualmente riporta non chiedono niente a
 * nessuno, e mostrarle come «Scadute» sarebbe solo rumore. Lo decide il server
 * (`pagamento.autofattura`); una fattura emessa non ancora accettata tiene la
 * scheda, perché il suo piano è vero e si può ancora correggere.
 */
export function showsInstalments(invoice) {
  return invoice?.pagamento?.autofattura !== true;
}

const BADGES = Object.freeze({
  saldata: { key: 'invoices.pagamento.stato.saldata', cls: 'badge-green' },
  parziale: { key: 'invoices.pagamento.stato.parziale', cls: 'badge-yellow' },
  scaduta: { key: 'invoices.pagamento.stato.scaduta', cls: 'badge-red' },
  senzaScadenze: { key: 'invoices.pagamento.stato.senzaScadenze', cls: 'badge-gray' },
});

/**
 * Il badge di uno stato. «Aperta» si legge per verso del denaro: da incassare
 * su una fattura emessa, da pagare su una ricevuta. Nessun badge quando il
 * pagamento non si applica.
 *
 * @returns {{key: string, cls: string} | null}
 */
export function paymentBadge(pagamento, direction) {
  const stato = pagamento?.stato;
  if (!stato || stato === 'nonApplicabile') return null;
  if (stato === 'aperta') {
    return {
      key: direction === 'inbound' ? 'invoices.pagamento.stato.daPagare' : 'invoices.pagamento.stato.daIncassare',
      cls: 'badge-cyan',
    };
  }
  return BADGES[stato] ?? null;
}

/** Somma col segno: una nota di credito toglie invece di aggiungere. */
function signedResiduoCents(rows) {
  let cents = 0;
  for (const row of rows) {
    const p = row?.pagamento;
    if (!p || p.residuo == null) continue;
    cents += (p.segno ?? 1) * Math.round(Number(p.residuo) * 100);
  }
  return cents;
}

/**
 * La riga dei totali: quante fatture sono mostrate e quanto resta da saldare,
 * e se l'elenco si è fermato al limite della richiesta.
 *
 * Il conteggio e la somma sono sulle righe mostrate; «parziale» si decide su
 * quante ne ha restituite il server (`fetchedCount`), prima dei filtri che la
 * pagina applica da sé: 500 righe ricevute e 3 mostrate da «Da controllare»
 * sono comunque un elenco che si è fermato a 500.
 *
 * @param {Array<object>} rows - le righe mostrate
 * @param {{fetchedCount?: number, limit?: number|null}} [opts]
 */
export function paymentTotals(rows, { fetchedCount = rows.length, limit = null } = {}) {
  return {
    count: rows.length,
    residuo: signedResiduoCents(rows) / 100,
    partial: Number.isInteger(limit) && fetchedCount >= limit,
  };
}

/** Le righe su cui si può registrare un pagamento. */
export function openRows(rows) {
  return rows.filter((row) => isPaymentOpen(row?.pagamento));
}

/** Il corpo di POST …/payments con solo quello che l'operatore ha compilato. */
export function paymentRequestBody({ dataPagamento, importo, note, residuoAtteso, metodo }) {
  const body = { dataPagamento, residuoAtteso: Number(residuoAtteso) };
  if (importo !== '' && importo !== null && importo !== undefined && !Number.isNaN(Number(importo))) {
    body.importo = Number(importo);
  }
  const text = typeof note === 'string' ? note.trim() : '';
  if (text) body.note = text;
  if (METODI_EFFETTIVI.includes(metodo)) body.metodo = metodo;
  return body;
}

/**
 * Il residuo nuovo di un rifiuto `409 payment_residual_changed`, oppure null.
 *
 * Il residuo può cambiare senza che nessuno abbia pagato: una fattura senza
 * rate le legge dall'XML archiviato al momento del pagamento, e quel piano può
 * chiedere meno del totale (una ritenuta, per esempio). Il server risponde con
 * il residuo e il riepilogo come li ha trovati; chi chiama li propone di nuovo
 * all'operatore, che conferma. Mai un nuovo tentativo automatico.
 *
 * Un residuo di zero è un caso a parte (`settled`): qualcun altro ha saldato la
 * fattura nel frattempo. Non c'è niente da riproporre — un importo di zero il
 * server lo rifiuta — c'è solo da dirlo e ricaricare.
 *
 * @param {Error & {status?: number, code?: string, residuo?: number, pagamento?: object}} err
 * @returns {{residuo: number, pagamento: object|null, settled: boolean} | null}
 */
export function changedResidual(err) {
  if (err?.status !== 409 || err.code !== 'payment_residual_changed' || typeof err.residuo !== 'number') {
    return null;
  }
  return { residuo: err.residuo, pagamento: err.pagamento ?? null, settled: err.residuo <= 0 };
}

/**
 * Registra più pagamenti, uno dopo l'altro, ciascuno per l'intero residuo.
 *
 * Non esiste un endpoint «in blocco»: ogni fattura è accettata o rifiutata per
 * conto suo, e un rifiuto non ferma le altre. Nessun nuovo tentativo
 * automatico: se il residuo è cambiato, lo deve guardare una persona.
 *
 * @param {Array<object>} rows - righe dell'elenco, con `pagamento`
 * @param {{dataPagamento: string, metodo?: string, record: (row: object, body: object) => Promise<unknown>}} opts
 */
export async function recordPaymentsSequentially(rows, { dataPagamento, metodo = '', record }) {
  const recorded = [];
  const failed = [];
  for (const row of rows) {
    try {
      await record(row, {
        dataPagamento,
        residuoAtteso: row.pagamento.residuo,
        ...(METODI_EFFETTIVI.includes(metodo) ? { metodo } : {}),
      });
      recorded.push(row);
    } catch (err) {
      failed.push({
        row,
        message: err?.message || String(err),
        code: err?.code ?? null,
        residuo: changedResidual(err)?.residuo ?? null,
        settled: changedResidual(err)?.settled ?? false,
      });
    }
  }
  return { recorded, failed };
}
